/**
 * canvas-engine/presets/canvasPresets.js
 *
 * OWN Canvas_Preset registry + instantiation (Task 3.4).
 *
 * One registry entry per section type across every family
 * (education / waas / flexOffice / retail / generic / chrome / custom /
 * dynamic). Each entry is `{ id, label, category, domain?, description?,
 * sectionType, canvas }` where `canvas` is the decomposed default-look
 * Canvas_Model produced by the own `decomposeSection` engine (Task 3.2/3.3)
 * from the transcribed presetData VALUES (Task 3.1).
 *
 * The own `instantiatePreset(id, opts)` / `createSectionFromPreset(id, opts)`
 * deep-clones the preset canvas into a brand-new section
 * `{ id, type: 'customCanvas', presetId, content: { canvas } }`, re-runs
 * `normalizeCanvas`, and rewrites every element id (and the section id) to a
 * fresh unique token so repeated insertions never collide and ids differ
 * across instantiations (Req 4.6, 5.7). The returned section shares NO
 * references with the registry constant (deep clone) (Req 4.5, 9.2).
 *
 * ZERO-REUSE CONSTRAINT: imports ONLY sibling engine modules
 * (`../model/canvasModel`, `./decompositionAdapters`) and the transcribed
 * `./presetData` VALUES. No React, no DOM. The registry is built once,
 * deterministically, at module load.
 *
 * Requirements: 4.5 (library item = fully editable customCanvas), 4.6 (fresh
 * unique ids per insertion), 5.7 (every section type registered, default look),
 * 9.2 (inserting a preset creates a new editable canvas section).
 */

import { normalizeCanvas, validateCanvas } from '../model/canvasModel';
import { decomposeSection } from './decompositionAdapters';
import {
  EDUCATION_PRESETS,
  WAAS_PRESETS,
  FLEX_OFFICE_PRESETS,
  RETAIL_PRESETS,
  GENERIC_PRESETS,
  CHROME_PRESETS,
  CUSTOM_PRESETS,
  DYNAMIC_PRESETS,
} from './presetData';

// ── Small pure helpers ──────────────────────────────────────────────────────

/** True for a non-null, non-array plain object. */
function isPlainObject(v) {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Trimmed string, or '' when not a usable string. */
function pickString(v) {
  return typeof v === 'string' ? v.trim() : '';
}

/** Deep clone of any JSON-shaped value, never sharing references with input. */
function deepClone(v) {
  if (Array.isArray(v)) return v.map(deepClone);
  if (isPlainObject(v)) {
    const out = {};
    const keys = Object.keys(v);
    for (let i = 0; i < keys.length; i += 1) out[keys[i]] = deepClone(v[keys[i]]);
    return out;
  }
  return v;
}

/**
 * Fresh-id generator. A module-local monotonic counter (`ID_SEQUENCE`)
 * guarantees uniqueness across EVERY call in the process — even within a single
 * instantiation and across repeated instantiations of the same preset — because
 * each token embeds a distinct, ever-increasing sequence value. The timestamp
 * and random suffix only add readability / cross-process spread; the counter
 * alone is what makes collisions impossible.
 */
let ID_SEQUENCE = 0;
function freshId(prefix) {
  ID_SEQUENCE += 1;
  const seq = ID_SEQUENCE.toString(36);
  const time = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix || 'el'}-${time}-${seq}${rand}`;
}

/** A concise human description for a preset, derived from its descriptor. */
function describe(data) {
  if (!isPlainObject(data)) return undefined;
  const content = isPlainObject(data.content) ? data.content : {};
  const candidate = pickString(content.subheading) || pickString(content.subtitle) || pickString(data.description);
  if (!candidate) return undefined;
  return candidate.length > 160 ? `${candidate.slice(0, 157)}...` : candidate;
}

// ── Registry construction ────────────────────────────────────────────────────

/**
 * Build one normalized registry entry. `canvas` is the decomposed default-look
 * model for `sectionType` (with optional adapter opts, e.g. chrome `domain`).
 */
function buildEntry({ id, label, category, domain, data, sectionType, decomposeOpts }) {
  const canvas = normalizeCanvas(decomposeSection(sectionType, decomposeOpts || {}));
  const entry = {
    id,
    label: label || sectionType,
    category,
    sectionType,
    canvas,
  };
  if (domain) entry.domain = domain;
  const description = describe(data);
  if (description) entry.description = description;
  // Store the preset's default content/style/layout so callers can seed a
  // native-typed section with the right default data (used by SectionLibraryPanel
  // when inserting a section from the library). Deep-cloned to prevent mutation.
  if (isPlainObject(data)) {
    if (isPlainObject(data.content)) entry.defaultContent = deepClone(data.content);
    if (isPlainObject(data.style))   entry.defaultStyle   = deepClone(data.style);
    if (isPlainObject(data.layout))  entry.defaultLayout  = deepClone(data.layout);
  }
  return Object.freeze(entry);
}

/**
 * Enumerate a family presetData map (education / waas / flexOffice / retail /
 * generic / dynamic / custom) into registry entries. The entry id and routing
 * sectionType come from each descriptor's own `sectionType`. For the real
 * domain families category = the descriptor `domain`; for the cross-domain
 * `dynamic` / `custom` families the category is fixed to the family name (their
 * descriptors carry a presentational `domain` like `generic`).
 */
function entriesFromFamilyMap(presetMap, defaultCategory) {
  const familyOwnsCategory = defaultCategory === 'dynamic' || defaultCategory === 'custom';
  const map = isPlainObject(presetMap) ? presetMap : {};
  const out = [];
  const keys = Object.keys(map);
  for (let i = 0; i < keys.length; i += 1) {
    const data = map[keys[i]];
    if (!isPlainObject(data)) continue;
    const sectionType = pickString(data.sectionType) || pickString(keys[i]);
    if (!sectionType) continue;
    const domain = pickString(data.domain) || defaultCategory;
    out.push(
      buildEntry({
        id: sectionType,
        label: pickString(data.label),
        category: familyOwnsCategory ? defaultCategory : domain || defaultCategory,
        domain,
        data,
        sectionType,
      }),
    );
  }
  return out;
}

/**
 * Enumerate the chrome presets (header / footer per domain). Each domain's
 * header and footer becomes its own entry; the section type stays
 * `header` / `footer` and the active chrome `domain` is passed to the adapter so
 * the decomposed look matches that domain's default chrome.
 */
function entriesFromChrome(chromeMap) {
  const map = isPlainObject(chromeMap) ? chromeMap : {};
  const out = [];
  const domains = Object.keys(map);
  for (let i = 0; i < domains.length; i += 1) {
    const domain = domains[i];
    const group = map[domain];
    if (!isPlainObject(group)) continue;
    const parts = ['header', 'footer'];
    for (let p = 0; p < parts.length; p += 1) {
      const sectionType = parts[p];
      const data = group[sectionType];
      if (!isPlainObject(data)) continue;
      out.push(
        buildEntry({
          id: `${domain}-${sectionType}`,
          label: pickString(data.label) || `${domain} ${sectionType}`,
          category: 'chrome',
          domain,
          data,
          sectionType,
          decomposeOpts: { domain, sectionType },
        }),
      );
    }
  }
  return out;
}

/** Build the full registry once, deterministically, at module load. */
function buildRegistry() {
  const all = [
    ...entriesFromFamilyMap(EDUCATION_PRESETS, 'education'),
    ...entriesFromFamilyMap(WAAS_PRESETS, 'waas'),
    ...entriesFromFamilyMap(FLEX_OFFICE_PRESETS, 'flexOffice'),
    ...entriesFromFamilyMap(RETAIL_PRESETS, 'retail'),
    ...entriesFromFamilyMap(GENERIC_PRESETS, 'generic'),
    ...entriesFromFamilyMap(DYNAMIC_PRESETS, 'dynamic'),
    ...entriesFromChrome(CHROME_PRESETS),
    ...entriesFromFamilyMap(CUSTOM_PRESETS, 'custom'),
  ];

  // Guarantee unique ids deterministically (defensive — section types are
  // already distinct per family, chrome ids are domain-prefixed).
  const seen = new Set();
  const deduped = [];
  for (let i = 0; i < all.length; i += 1) {
    const entry = all[i];
    let id = entry.id;
    let n = 2;
    while (seen.has(id)) {
      id = `${entry.id}-${n}`;
      n += 1;
    }
    seen.add(id);
    deduped.push(id === entry.id ? entry : Object.freeze({ ...entry, id }));
  }
  return deduped;
}

/**
 * CANVAS_PRESETS — the own Canvas_Preset registry: one entry per section type
 * across every family. Each entry: `{ id, label, category, domain?,
 * description?, sectionType, canvas }`. (Req 5.7)
 */
export const CANVAS_PRESETS = Object.freeze(buildRegistry());

/** Internal id → entry index for O(1) lookup. */
const PRESET_INDEX = (() => {
  const map = new Map();
  for (let i = 0; i < CANVAS_PRESETS.length; i += 1) {
    map.set(CANVAS_PRESETS[i].id, CANVAS_PRESETS[i]);
  }
  return map;
})();

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Return the registry entry for `id`, or `undefined` when no preset matches.
 *
 * @param {string} id a Canvas_Preset id
 * @returns {object|undefined} the (frozen) registry entry
 */
export function getPreset(id) {
  return PRESET_INDEX.get(pickString(id)) || undefined;
}

/**
 * Return registry presets for the Section_Library, optionally filtered.
 *
 * @param {(string|object)} [filter] a category/domain string, or
 *   `{ category?, domain? }`. A string matches either `category` or `domain`.
 * @returns {object[]} a new array of matching entries (registry order)
 */
export function listPresets(filter) {
  if (filter == null || filter === '') return CANVAS_PRESETS.slice();

  let wantCategory = '';
  let wantDomain = '';
  if (typeof filter === 'string') {
    const f = filter.trim();
    return CANVAS_PRESETS.filter((p) => p.category === f || p.domain === f);
  }
  if (isPlainObject(filter)) {
    wantCategory = pickString(filter.category);
    wantDomain = pickString(filter.domain);
  }
  return CANVAS_PRESETS.filter((p) => {
    if (wantCategory && p.category !== wantCategory) return false;
    if (wantDomain && p.domain !== wantDomain) return false;
    return true;
  });
}

/**
 * Instantiate a Canvas_Preset into a brand-new, fully-editable section.
 *
 * Deep-clones the preset's canvas (no shared references with the registry
 * constant), re-runs `normalizeCanvas` for a guaranteed-valid model, then
 * rewrites EVERY element id — and the section id — to a fresh unique token via
 * the monotonic `freshId` counter, so repeated insertions never collide and ids
 * differ across instantiations. (Req 4.5, 4.6, 5.7, 9.2)
 *
 * @param {string} id a Canvas_Preset id
 * @param {object} [opts] optional `{ id }` to force the new section id
 * @returns {object|undefined} a new section
 *   `{ id, type: 'customCanvas', presetId, content: { canvas } }`, or
 *   `undefined` when `id` does not match a preset
 */
export function instantiatePreset(id, opts = {}) {
  const entry = getPreset(id);
  if (!entry) return undefined;

  // Deep clone first so we never share references with the registry constant,
  // then normalize for a guaranteed-valid model.
  const canvas = normalizeCanvas(deepClone(entry.canvas));

  // Rewrite every element id to a fresh, globally-unique token.
  if (Array.isArray(canvas.elements)) {
    for (let i = 0; i < canvas.elements.length; i += 1) {
      const el = canvas.elements[i];
      if (isPlainObject(el)) {
        el.id = freshId(el.type || 'el');
      }
    }
  }

  const options = isPlainObject(opts) ? opts : {};
  const sectionId = pickString(options.id) || freshId('section');

  return {
    id: sectionId,
    type: 'customCanvas',
    presetId: entry.id,
    content: { canvas },
  };
}

/** Alias of `instantiatePreset` for callers using the section-factory name. */
export function createSectionFromPreset(id, opts) {
  return instantiatePreset(id, opts);
}

// Re-export for convenience so library code can validate an instantiated canvas
// without a second model import.
export { validateCanvas };

export default CANVAS_PRESETS;
